/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * This file is part of AksharJobs application.
 */

import { buildApiUrl, makeAuthenticatedRequest } from '../config/api';

const parseJson = async (response) => {
  if (!response) {
    throw new Error('No response from server');
  }
  const data = await response.json();
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || 'Request failed');
  }
  return data?.data || data;
};

export const getGalleryImageUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalized = path.startsWith('/') ? path.replace(/^\/+/, '') : path;
  if (normalized.startsWith('uploads/')) {
    const uploadPath = normalized.replace(/^uploads\//, '');
    return buildApiUrl(`/api/users/uploads/${uploadPath}`);
  }
  return buildApiUrl(path.startsWith('/') ? path : `/${path}`);
};

export const fetchPublicGallery = async () => {
  const response = await fetch(buildApiUrl('/api/public/gallery'));
  if (!response.ok) {
    throw new Error('Unable to load gallery');
  }
  const payload = await response.json();
  return payload?.data || [];
};

export const fetchAdminGallery = async () => {
  const response = await makeAuthenticatedRequest(buildApiUrl('/api/admin/gallery'));
  return parseJson(response);
};

const buildFormData = (values) => {
  const formData = new FormData();
  const eventTitle = values.eventTitle || values.title || '';
  formData.append('title', values.title || eventTitle);
  formData.append('eventTitle', eventTitle);
  formData.append('category', values.category || 'expo');
  formData.append('location', values.location || '');
  formData.append('date', values.date || '');
  formData.append('size', values.size || 'normal');
  formData.append('isVisible', values.isVisible ? 'true' : 'false');
  formData.append('featured', values.featured ? 'true' : 'false');
  if (values.displayOrder) {
    formData.append('displayOrder', values.displayOrder);
  }
  if (values.imageFile instanceof File) {
    formData.append('image', values.imageFile);
  }
  return formData;
};

export const createGalleryItem = async (values) => {
  const response = await makeAuthenticatedRequest(buildApiUrl('/api/admin/gallery'), {
    method: 'POST',
    body: buildFormData(values)
  });
  return parseJson(response);
};

export const createGalleryItemsBulk = async (items, { onProgress } = {}) => {
  const created = [];
  const errors = [];

  for (let index = 0; index < items.length; index += 1) {
    try {
      const item = await createGalleryItem(items[index]);
      created.push(item);
      onProgress?.(index + 1, items.length);
    } catch (error) {
      errors.push({
        fileName: items[index].imageFile?.name || `Photo ${index + 1}`,
        message: error.message || 'Upload failed'
      });
    }
  }

  if (created.length === 0 && errors.length > 0) {
    throw new Error(errors.map((entry) => `${entry.fileName}: ${entry.message}`).join('; '));
  }

  return { created, errors };
};

export const updateGalleryItem = async (id, values) => {
  const response = await makeAuthenticatedRequest(buildApiUrl(`/api/admin/gallery/${id}`), {
    method: 'PUT',
    body: buildFormData(values)
  });
  return parseJson(response);
};

export const deleteGalleryItem = async (id) => {
  const response = await makeAuthenticatedRequest(buildApiUrl(`/api/admin/gallery/${id}`), {
    method: 'DELETE'
  });
  await parseJson(response);
};

export const toggleGalleryVisibility = async (id) => {
  const response = await makeAuthenticatedRequest(
    buildApiUrl(`/api/admin/gallery/${id}/visibility`),
    { method: 'PATCH' }
  );
  return parseJson(response);
};
